import { Container, Graphics, Text, TextStyle, Sprite, Filter, Texture, Assets } from 'pixi.js';
import { TILE_SIZE, TILE_SCALE } from '../config.js';
import { UIManager } from './ui/UIManager.js';
import { waterVertexShader, waterFragmentShader, waterShaderUniforms } from '../shaders/waterShader.js';
import { generateWaterTextures } from '../utils/textureGenerator.js';

// Game states
const GameState = {
    MENU: 'MENU',
    LOADING: 'LOADING',
    PLAYING: 'PLAYING',
    PAUSED: 'PAUSED'
};

export class MenuSystem {
    constructor(app) {
        this.app = app;
        this.menuContainer = new Container();
        this.menuContainer.zIndex = 10000; // Ensure menu is on top
        this.isVisible = false;
        
        // Get audio manager from asset manager if available
        this.audioManager = app.assetManager?.getAudioManager();
        
        // Create water background
        this.createWaterBackground();
        
        // Create floating items container at root level
        this.floatingItemsContainer = new Container();
        this.floatingItemsContainer.zIndex = 9999;
        this.app.stage.addChild(this.floatingItemsContainer);
        
        // Initialize floating items
        this.floatingItems = [];
        this.loadFloatingItems();
        
        // Create credits container
        this.creditsContainer = new Container();
        this.creditsContainer.zIndex = 10001; // Above menu
        this.creditsContainer.visible = false;
        this.app.stage.addChild(this.creditsContainer);
        
        // Create menu background with pixel art styling
        this.background = new Graphics();
        
        // Retro terminal background
        this.background.fill({ color: 0x0A0A0A });
        this.background.rect(0, 0, 450, 550);
        
        // Add scan line effect
        for (let i = 0; i < 550; i += 3) {
            this.background.fill({ color: 0x00FF00, alpha: 0.05 });
            this.background.rect(0, i, 450, 1);
        }
        
        // Pixel border pattern
        this.background.setStrokeStyle({ width: 2, color: 0x00FF00 });
        this.background.rect(0, 0, 450, 550);
        
        // Inner border for depth
        this.background.setStrokeStyle({ width: 1, color: 0x00AA00 });
        this.background.rect(2, 2, 446, 546);
        
        // Corner pixel decorations
        const cornerSize = 10;
        this.background.fill({ color: 0x00FF00 });
        // Top-left
        this.background.rect(0, 0, cornerSize, 2);
        this.background.rect(0, 0, 2, cornerSize);
        // Top-right
        this.background.rect(450 - cornerSize, 0, cornerSize, 2);
        this.background.rect(450 - 2, 0, 2, cornerSize);
        // Bottom-left
        this.background.rect(0, 550 - 2, cornerSize, 2);
        this.background.rect(0, 550 - cornerSize, 2, cornerSize);
        // Bottom-right
        this.background.rect(450 - cornerSize, 550 - 2, cornerSize, 2);
        this.background.rect(450 - 2, 550 - 2, 2, cornerSize);
        
        this.menuContainer.addChild(this.background);
        
        // Create title with pixel art typography
        const titleStyle = new TextStyle({
            fontFamily: '"Press Start 2P", "Courier New", monospace',
            fontSize: 36,
            fontWeight: 'normal',
            fill: 0x00FFFF, // Cyan for titles
            align: 'center',
            letterSpacing: 2
        });
        
        this.title = new Text('Island Boy', titleStyle);
        this.title.x = 225;
        this.title.y = 60;
        this.title.anchor.set(0.5);
        this.menuContainer.addChild(this.title);
        
        // Add subtitle
        const subtitleStyle = new TextStyle({
            fontFamily: '"Press Start 2P", "Courier New", monospace',
            fontSize: 12,
            fontWeight: 'normal',
            fill: 0x888888, // Gray for subtitle
            align: 'center',
            letterSpacing: 1
        });
        
        this.subtitle = new Text('ECOSYSTEM SURVIVAL', subtitleStyle);
        this.subtitle.x = 225;
        this.subtitle.y = 110;
        this.subtitle.anchor.set(0.5);
        this.menuContainer.addChild(this.subtitle);
        
        // Create menu buttons with pixel art styling
        this.createPixelMenuButtons();
    }
    
    createPixelMenuButtons() {
        // Pixel art button style
        const buttonStyle = new TextStyle({
            fontFamily: '"Press Start 2P", "Courier New", monospace',
            fontSize: 14,
            fontWeight: 'normal',
            fill: 0x00FF00, // Classic green for buttons
            align: 'center',
            letterSpacing: 1
        });
        
        // Create buttons array
        this.buttons = [];
        const buttonLabels = ['START GAME', 'SIMULATION', 'OPTIONS', 'CREDITS', 'EXIT'];
        const buttonY = 180;
        const buttonSpacing = 70;
        
        buttonLabels.forEach((label, index) => {
            const button = this.createPixelButton(label, 225, buttonY + (index * buttonSpacing), buttonStyle);
            this.buttons.push(button);
            this.menuContainer.addChild(button);
        });
    }
    
    createPixelButton(label, x, y, style) {
        const button = new Container();
        
        // Pixel art button background
        const bg = new Graphics();
        bg.fill({ color: 0x0F0F0F });
        bg.rect(-100, -20, 200, 40);
        
        // Pixel border
        bg.setStrokeStyle({ width: 2, color: 0x00FF00 });
        bg.rect(-100, -20, 200, 40);
        
        // Inner border
        bg.setStrokeStyle({ width: 1, color: 0x00AA00 });
        bg.rect(-98, -18, 196, 36);
        
        // Corner decorations
        const cornerSize = 6;
        bg.fill({ color: 0x00FF00 });
        // Top-left
        bg.rect(-100, -20, cornerSize, 2);
        bg.rect(-100, -20, 2, cornerSize);
        // Top-right
        bg.rect(100 - cornerSize, -20, cornerSize, 2);
        bg.rect(100 - 2, -20, 2, cornerSize);
        // Bottom-left
        bg.rect(-100, 20 - 2, cornerSize, 2);
        bg.rect(-100, 20 - cornerSize, 2, cornerSize);
        // Bottom-right
        bg.rect(100 - cornerSize, 20 - 2, cornerSize, 2);
        bg.rect(100 - 2, 20 - cornerSize, 2, cornerSize);
        
        button.addChild(bg);
        
        // Button text
        const text = new Text(label, style);
        text.anchor.set(0.5);
        button.addChild(text);
        
        // Position button
        button.position.set(x, y);
        
        // Make interactive
        button.eventMode = 'static';
        button.cursor = 'pointer';
        
        // Add pixel art hover effects
        button.on('pointerover', () => {
            bg.fill({ color: 0x00FF00, alpha: 0.2 });
            bg.rect(-98, -18, 196, 36);
            text.style.fill = 0x00FFFF; // Cyan on hover
        });
        
        button.on('pointerout', () => {
            bg.fill({ color: 0x000000, alpha: 0 });
            bg.rect(-98, -18, 196, 36);
            text.style.fill = 0x00FF00; // Back to green
        });
        
        button.on('pointerdown', () => {
            bg.fill({ color: 0x00AA00, alpha: 0.3 });
            bg.rect(-98, -18, 196, 36);
        });
        
        // Add click handlers based on button label
        button.on('pointerup', () => {
            switch(label) {
                case 'START GAME':
                    this.startGame();
                    break;
                case 'SIMULATION':
                    this.startSimulation();
                    break;
                case 'OPTIONS':
                    this.showOptions();
                    break;
                case 'CREDITS':
                    this.showCredits();
                    break;
                case 'EXIT':
                    this.exitGame();
                    break;
            }
        });
        
        return button;
    }
    
    show() {
        console.log('Showing main menu');
        this.menuContainer.visible = true;
        this.positionMenu();
        this.isVisible = true;
        
        // Play menu music if available
        if (this.audioManager) {
            this.audioManager.playMusic('menu_music', true);
        }
    }
    
    positionMenu() {
        // Center menu on screen
        this.menuContainer.x = (this.app.screen.width - 450) / 2;
        this.menuContainer.y = (this.app.screen.height - 550) / 2;
    }
    
    hide() {
        console.log('Hiding main menu');
        this.menuContainer.visible = false;
        this.isVisible = false;
        
        // Stop menu music
        if (this.audioManager) {
            this.audioManager.stopMusic();
        }
    }
    
    destroy() {
        // Clean up menu resources
        if (this.menuContainer) {
            this.menuContainer.destroy({ children: true });
        }
        if (this.floatingItemsContainer) {
            this.floatingItemsContainer.destroy({ children: true });
        }
        if (this.creditsContainer) {
            this.creditsContainer.destroy({ children: true });
        }
    }
    
    async loadMenuSounds() {
        try {
            console.log('[MENU] Loading menu sounds...');
            await this.audioManager.loadSound('assets/audio/menu-music.wav', 'menu_music');
            await this.audioManager.loadSound('assets/audio/button-click.mp3', 'button_click');
            await this.audioManager.loadSound('assets/audio/button-hover.mp3', 'button_hover');
            console.log('[MENU] Menu sounds loaded successfully');
        } catch (error) {
            console.warn('[MENU] Failed to load menu sounds:', error);
        }
    }
    
    createMenuItem(text, y) {
        // Create HTML button
        const button = document.createElement('button');
        button.textContent = text;
        button.style.width = '300px';
        button.style.height = '50px';
        button.style.margin = '10px';
        button.style.backgroundColor = '#333';
        button.style.color = 'white';
        button.style.border = 'none';
        button.style.borderRadius = '10px';
        button.style.fontFamily = '"Press Start 2P"';
        button.style.fontSize = '16px';
        button.style.cursor = 'pointer';
        button.style.transition = 'background-color 0.2s';
        button.style.pointerEvents = 'auto'; // Enable pointer events only for the button
        
        // Add hover effect
        button.onmouseover = () => {
            button.style.backgroundColor = '#666';
            this.audioManager.playGlobalSound('button_hover', { volume: 0.4 });
        };
        
        button.onmouseout = () => {
            button.style.backgroundColor = '#333';
        };
        
        // Add click handler
        button.onclick = async (e) => {
            e.stopPropagation(); // Prevent event from bubbling to container
            this.audioManager.playGlobalSound('button_click', { volume: 0.4 });
            
            if (text === 'Start Game') {
                console.log('[MENU] Starting game...');
                try {
                    // Fade out menu music before cleanup
                    this.audioManager.fadeOutGlobalSound('menu_music', 0.0);
                    
                    // Clean up menu
                    this.cleanup();
                    
                    // Dispatch start game event
                    console.log('[MENU] Dispatching startGame event');
                    const startEvent = new CustomEvent('startGame', {
                        detail: { app: this.app }
                    });
                    window.dispatchEvent(startEvent);
                } catch (error) {
                    console.error('[MENU] Error starting game:', error);
                }
            } else if (text === 'Options') {
                console.log('Opening options...');
                this.hide();
                this.uiManager.showOptions();
            } else if (text === 'Credits') {
                console.log('Showing credits...');
                this.showCredits();
            }
        };
        
        this.buttonsContainer.appendChild(button);
    }
    
    handleResize() {
        if (!this.menuContainer) return; // Guard against null reference
        
        this.menuContainer.x = (this.app.screen.width - 400) / 2;
        this.menuContainer.y = (this.app.screen.height - 500) / 2;
        
        if (this.buttonsContainer) {
            this.buttonsContainer.style.top = '50%';
            this.buttonsContainer.style.left = '50%';
        }
        
        // Update floating shapes container position
        if (this.floatingItemsContainer) {
            this.floatingItemsContainer.x = 0;
            this.floatingItemsContainer.y = 0;
        }
        
        // Update water background size
        if (this.waterBackground) {
            this.waterBackground.width = window.innerWidth;
            this.waterBackground.height = window.innerHeight;
        }
    }
    
    createFloatingShapes() {
        // Create 10 floating shapes with different properties
        for (let i = 0; i < 10; i++) {
            const shape = new Graphics();
            const size = 30 + Math.random() * 50; // Larger shapes
            const x = Math.random() * (window.innerWidth - size);
            const y = Math.random() * (window.innerHeight - size);
            const speedX = (Math.random() - 0.5) * 2; // Faster initial speed
            const speedY = (Math.random() - 0.5) * 2; // Faster initial speed
            const color = 0xFFFFFF;
            const alpha = 0.05 + Math.random() * 0.1; // More transparent
            
            // Draw different shapes
            if (i % 3 === 0) {
                // Circle
                shape.beginFill(color, alpha);
                shape.drawCircle(0, 0, size / 2);
                shape.endFill();
            } else if (i % 3 === 1) {
                // Square
                shape.beginFill(color, alpha);
                shape.drawRect(-size / 2, -size / 2, size, size);
                shape.endFill();
            } else {
                // Triangle
                shape.beginFill(color, alpha);
                shape.moveTo(0, -size / 2);
                shape.lineTo(size / 2, size / 2);
                shape.lineTo(-size / 2, size / 2);
                shape.endFill();
            }
            
            shape.x = x;
            shape.y = y;
            
            // Store shape properties for animation
            shape.speedX = speedX;
            shape.speedY = speedY;
            shape.size = size;
            
            this.floatingItemsContainer.addChild(shape);
        }
        
        // Start animation
        this.app.ticker.add(this.updateFloatingShapes, this);
    }
    
    updateFloatingShapes() {
        if (!this.isVisible) return;
        
        // Update each shape's position
        this.floatingItemsContainer.children.forEach(shape => {
            // Update position
            shape.x += shape.speedX;
            shape.y += shape.speedY;
            
            // Bounce off screen edges with smooth deceleration
            if (shape.x <= 0) {
                shape.x = 0;
                shape.speedX = Math.abs(shape.speedX) * 0.95; // Slight energy loss
            } else if (shape.x >= window.innerWidth - shape.size) {
                shape.x = window.innerWidth - shape.size;
                shape.speedX = -Math.abs(shape.speedX) * 0.95;
            }
            
            if (shape.y <= 0) {
                shape.y = 0;
                shape.speedY = Math.abs(shape.speedY) * 0.95;
            } else if (shape.y >= window.innerHeight - shape.size) {
                shape.y = window.innerHeight - shape.size;
                shape.speedY = -Math.abs(shape.speedY) * 0.95;
            }
            
            // Add slight random movement for more natural bouncing
            shape.speedX += (Math.random() - 0.5) * 0.1;
            shape.speedY += (Math.random() - 0.5) * 0.1;
            
            // Limit maximum speed
            const maxSpeed = 3;
            shape.speedX = Math.max(Math.min(shape.speedX, maxSpeed), -maxSpeed);
            shape.speedY = Math.max(Math.min(shape.speedY, maxSpeed), -maxSpeed);
        });
    }
    
    show() {
        console.log('Showing main menu');
        this.menuContainer.visible = true;
        this.positionMenu();
        this.isVisible = true;
        
        // Show marquee text if it exists
        if (this.marqueeText) {
            this.marqueeText.visible = true;
        }
        
        // Play menu music if available
        if (this.audioManager) {
            this.audioManager.playMusic('menu_music', true);
        }
    }
    
    hide() {
        console.log('Hiding main menu');
        this.menuContainer.visible = false;
        this.isVisible = false;
        
        // Hide marquee text if it exists
        if (this.marqueeText) {
            this.marqueeText.visible = false;
        }
        
        // Stop menu music
        if (this.audioManager) {
            this.audioManager.stopMusic();
        }
    }
    
    startGame() {
        console.log('Starting game...');
        this.hide();
        
        // Trigger game start
        if (this.app.startGame) {
            this.app.startGame();
        }
    }
    
    startSimulation() {
        console.log('Starting simulation mode...');
        this.hide();
        
        // Trigger simulation mode
        if (this.app.startSimulation) {
            this.app.startSimulation();
        } else {
            // Fallback - just start game with simulation system visible
            if (this.app.startGame) {
                this.app.startGame();
                // Show simulation system after game starts
                setTimeout(() => {
                    if (window.gameEngine && window.gameEngine.simulationSystem) {
                        console.log('[MENU] Simulation system available');
                    }
                }, 1000);
            }
        }
    }
    
    showOptions() {
        console.log('Showing options...');
        this.hide();
        
        // Show options menu
        if (this.app.uiManager) {
            this.app.uiManager.showOptions();
        }
    }
    
    showCredits() {
        console.log('Showing credits...');
        this.hide();
        this.creditsContainer.visible = true;
        this.createCreditsContent();
    }
    
    hideCredits() {
        console.log('Hiding credits...');
        this.creditsContainer.visible = false;
        this.show();
    }
    
    exitGame() {
        console.log('Exiting game...');
        
        // In a real game, this might close the window or return to a launcher
        if (confirm('Are you sure you want to exit?')) {
            window.close();
        }
    }
    
    createCreditsContent() {
        // Clear existing credits content
        this.creditsContainer.removeChildren();
        
        // Create credits background with pixel art style
        const creditsBg = new Graphics();
        creditsBg.fill({ color: 0x0A0A0A });
        creditsBg.rect(0, 0, 600, 400);
        
        // Pixel border
        creditsBg.setStrokeStyle({ width: 2, color: 0x00FFFF });
        creditsBg.rect(0, 0, 600, 400);
        
        // Inner border
        creditsBg.setStrokeStyle({ width: 1, color: 0x008888 });
        creditsBg.rect(2, 2, 596, 396);
        
        this.creditsContainer.addChild(creditsBg);
        
        // Create credits text with pixel art style
        const creditsStyle = new TextStyle({
            fontFamily: '"Press Start 2P", "Courier New", monospace',
            fontSize: 16,
            fontWeight: 'normal',
            fill: 0x00FFFF, // Cyan for credits
            align: 'center',
            letterSpacing: 1
        });
        
        const credits = [
            'ISLAND BOY',
            '',
            'ECOSYSTEM SURVIVAL',
            '',
            'Created with:',
            'PIXI.js, Bitecs, Modern Web Tech',
            '',
            'Featuring:',
            'Dynamic Ecosystem Simulation',
            'Pixel Art UI Design',
            'Environmental Events',
            '',
            'Thank you for playing!'
        ];
        
        credits.forEach((line, index) => {
            const text = new Text(line, creditsStyle);
            text.x = 300;
            text.y = 50 + (index * 25);
            text.anchor.set(0.5, 0);
            this.creditsContainer.addChild(text);
        });
        
        // Add back button with pixel art style
        const backButton = new Text('BACK TO MENU', new TextStyle({
            fontFamily: '"Press Start 2P", "Courier New", monospace',
            fontSize: 12,
            fontWeight: 'normal',
            fill: 0x00FF00, // Green for back button
            align: 'center',
            letterSpacing: 1
        }));
        backButton.x = 300;
        backButton.y = 350;
        backButton.anchor.set(0.5);
        backButton.eventMode = 'static';
        backButton.cursor = 'pointer';
        
        backButton.on('pointerover', () => {
            backButton.style.fill = 0x00FFFF; // Cyan on hover
        });
        
        backButton.on('pointerout', () => {
            backButton.style.fill = 0x00FF00; // Back to green
        });
        
        backButton.on('pointerdown', () => {
            this.hideCredits();
        });
        
        this.creditsContainer.addChild(backButton);
    }
    
    createWaterBackground() {
        // Create water background sprite
        this.waterBackground = new Sprite(Texture.WHITE);
        this.waterBackground.width = window.innerWidth;
        this.waterBackground.height = window.innerHeight;
        this.waterBackground.tint = 0x000000;
        this.waterBackground.zIndex = 9998; // Below floating shapes
        this.app.stage.addChild(this.waterBackground);
        
        // Generate water textures first
        const textures = generateWaterTextures();
        
        // Create water shader filter with initial uniforms
        const uniforms = {
            ...waterShaderUniforms,
            uWaterDepthGradient: Texture.from(textures.depthGradient),
            uCausticTexture: Texture.from(textures.caustics),
            uCausticHighlightTexture: Texture.from(textures.causticHighlight),
            uCausticNoiseTexture: Texture.from(textures.causticNoise),
            uCausticFadeNoiseTexture: Texture.from(textures.causticFadeNoise),
            uSpecularNoiseTexture: Texture.from(textures.specularNoise),
            uSpecularMovementLeftNoiseTexture: Texture.from(textures.specularMovementLeft),
            uSpecularMovementRightNoiseTexture: Texture.from(textures.specularMovementRight),
            uFoamTexture: Texture.from(textures.foam),
            time: 0.0 // Initialize time uniform
        };
        
        this.waterFilter = new Filter(waterVertexShader, waterFragmentShader, uniforms);
        
        // Apply filter to water background
        this.waterBackground.filters = [this.waterFilter];
        
        // Start animation
        this.app.ticker.add(this.updateWaterShader, this);
    }
    
    updateWaterShader(deltaTime) {
        if (!this.isVisible || !this.waterFilter || !this.waterFilter.uniforms) return;
        
        // Update shader time
        const currentTime = this.waterFilter.uniforms.time || 0;
        this.waterFilter.uniforms.time = currentTime + deltaTime * 0.01;
    }
    
    async loadFloatingItems() {
        try {
            // Define the images to load
            const images = [
                'assets/images/plants/cactus.png',
                'assets/images/plants/pine.png',
                'assets/images/plants/mushroom.png',
                'assets/images/plants/herb.png',
                'assets/images/plants/flower.png',
                'assets/images/plants/bush.png',
                'assets/images/rocks/rock_small.png',
                'assets/images/rocks/rock_medium.png',
                'assets/images/rocks/rock_large.png'
            ];
            
            // Load all images
            const textures = await Promise.all(images.map(path => Assets.load(path)));
            
            // Create 15 random floating items
            for (let i = 0; i < 15; i++) {
                const texture = textures[Math.floor(Math.random() * textures.length)];
                const item = new Sprite(texture);
                item.anchor.set(0.5);
                item.x = Math.random() * window.innerWidth;
                item.y = Math.random() * window.innerHeight;
                
                // Random scale between 0.1 and 0.3
                const scale = 0.1 + Math.random() * 0.2;
                item.scale.set(scale);
                
                // Initialize movement properties
                item.speedX = (Math.random() - 0.5) * 3;
                item.speedY = (Math.random() - 0.5) * 3;
                item.rotationSpeed = (Math.random() - 0.5) * 0.1;
                
                this.floatingItemsContainer.addChild(item);
                this.floatingItems.push(item);
            }
            
            // Start animation
            this.app.ticker.add(this.updateFloatingItems, this);
        } catch (error) {
            console.error('Failed to load floating items:', error);
        }
    }
    
    updateFloatingItems() {
        if (!this.isVisible) return;
        
        this.floatingItems.forEach(item => {
            // Update position
            item.x += item.speedX;
            item.y += item.speedY;
            
            // Update rotation
            item.rotation += item.rotationSpeed;
            
            // Bounce off screen edges
            if (item.x <= 0 || item.x >= window.innerWidth) {
                item.speedX *= -1;
                item.x = Math.max(0, Math.min(item.x, window.innerWidth));
            }
            
            if (item.y <= 0 || item.y >= window.innerHeight) {
                item.speedY *= -1;
                item.y = Math.max(0, Math.min(item.y, window.innerHeight));
            }
            
            // Add some random movement for more fun
            item.speedX += (Math.random() - 0.5) * 0.1;
            item.speedY += (Math.random() - 0.5) * 0.1;
            
            // Limit maximum speed
            const maxSpeed = 4;
            item.speedX = Math.max(Math.min(item.speedX, maxSpeed), -maxSpeed);
            item.speedY = Math.max(Math.min(item.speedY, maxSpeed), -maxSpeed);
        });
    }
    
    showCredits() {
        // Hide main menu elements but keep music playing
        this.menuContainer.visible = false;
        this.buttonsContainer.style.display = 'none';
        this.buttonsContainer.style.pointerEvents = 'none';
        
        // Clear previous credits content
        this.creditsContainer.removeChildren();
        
        // Create credits background
        const creditsBg = new Graphics();
        creditsBg.fill({ color: 0x000000, alpha: 0.9 });
        creditsBg.roundRect(0, 0, 600, 700, 20);
        this.creditsContainer.addChild(creditsBg);
        
        // Create credits title
        const titleStyle = new TextStyle({
            fontFamily: '"Press Start 2P"',
            fontSize: 32,
            fill: 0xFFFFFF,
            align: 'center'
        });
        
        const title = new Text('CREDITS', titleStyle);
        title.x = 300;
        title.y = 50;
        title.anchor.set(0.5);
        this.creditsContainer.addChild(title);
        
        // Create credits content
        const credits = [
            { role: 'Game Design & Development', name: 'Abhishek Shivakumar' },
            { role: 'Art & Animation', name: 'Abhishek Shivakumar' },
            { role: 'Music & Sound', name: 'Abhishek Shivakumar' },
            { role: 'Special Thanks', name: 'All Players!' },
            { role: 'Made with', name: '❤️ & PIXI.js' }
        ];
        
        // Create credit entries with animation
        credits.forEach((credit, index) => {
            const entry = new Container();
            // Start from left or right based on index
            const startX = index % 2 === 0 ? -300 : 900;
            entry.x = startX;
            entry.y = 150 + (index * 80);
            entry.alpha = 0;
            entry.scale.set(0.5);
            
            const roleStyle = new TextStyle({
                fontFamily: '"Press Start 2P"',
                fontSize: 16,
                fill: 0x88FF88,
                align: 'center'
            });
            
            const nameStyle = new TextStyle({
                fontFamily: '"Press Start 2P"',
                fontSize: 20,
                fill: 0xFFFFFF,
                align: 'center'
            });
            
            const role = new Text(credit.role, roleStyle);
            role.anchor.set(0.5);
            role.y = -20;
            
            const name = new Text(credit.name, nameStyle);
            name.anchor.set(0.5);
            name.y = 20;
            
            entry.addChild(role, name);
            this.creditsContainer.addChild(entry);
            
            // Animate entry
            this.app.ticker.add(() => {
                if (entry.alpha < 1) {
                    entry.alpha += 0.02;
                    entry.scale.x = Math.min(1, entry.scale.x + 0.02);
                    entry.scale.y = Math.min(1, entry.scale.y + 0.02);
                }
                
                // Slide to center
                const targetX = 300;
                const speed = 0.1;
                if (Math.abs(entry.x - targetX) > 1) {
                    entry.x += (targetX - entry.x) * speed;
                }
            });
        });
        
        // Create back button
        const backButton = document.createElement('button');
        backButton.textContent = 'Back to Menu';
        backButton.style.position = 'absolute';
        backButton.style.bottom = '50px';
        backButton.style.left = '50%';
        backButton.style.transform = 'translateX(-50%)';
        backButton.style.width = '200px';
        backButton.style.height = '40px';
        backButton.style.backgroundColor = '#333';
        backButton.style.color = 'white';
        backButton.style.border = 'none';
        backButton.style.borderRadius = '10px';
        backButton.style.fontFamily = '"Press Start 2P"';
        backButton.style.fontSize = '14px';
        backButton.style.cursor = 'pointer';
        backButton.style.transition = 'background-color 0.2s';
        
        backButton.onmouseover = () => {
            backButton.style.backgroundColor = '#666';
        };
        
        backButton.onmouseout = () => {
            backButton.style.backgroundColor = '#333';
        };
        
        backButton.onclick = () => {
            this.hideCredits();
            this.show();
        };
        
        document.body.appendChild(backButton);
        this.creditsBackButton = backButton;
        
        // Center credits container
        this.creditsContainer.x = (this.app.screen.width - 600) / 2;
        this.creditsContainer.y = (this.app.screen.height - 700) / 2;
        
        // Show credits
        this.creditsContainer.visible = true;
        
        // Add some floating game elements
        this.addCreditsFloatingElements();
    }
    
    hideCredits() {
        this.creditsContainer.visible = false;
        if (this.creditsBackButton && this.creditsBackButton.parentNode) {
            this.creditsBackButton.parentNode.removeChild(this.creditsBackButton);
        }
        // Remove floating elements
        this.creditsFloatingElements?.forEach(element => {
            if (element.parent) {
                element.parent.removeChild(element);
            }
        });
        this.creditsFloatingElements = [];
    }
    
    addCreditsFloatingElements() {
        this.creditsFloatingElements = [];
        
        // Create some fun floating elements
        for (let i = 0; i < 10; i++) {
            const element = new Graphics();
            const size = 20 + Math.random() * 30;
            const x = Math.random() * this.app.screen.width;
            const y = Math.random() * this.app.screen.height;
            
            // Draw different shapes
            const shapeType = Math.floor(Math.random() * 3);
            element.beginFill(0xFFFFFF, 0.2);
            
            switch (shapeType) {
                case 0: // Star
                    for (let j = 0; j < 5; j++) {
                        const angle = (j * 2 * Math.PI) / 5 - Math.PI / 2;
                        const nextAngle = ((j + 1) * 2 * Math.PI) / 5 - Math.PI / 2;
                        element.moveTo(0, 0);
                        element.lineTo(Math.cos(angle) * size, Math.sin(angle) * size);
                        element.lineTo(Math.cos((angle + nextAngle) / 2) * (size / 2), Math.sin((angle + nextAngle) / 2) * (size / 2));
                    }
                    break;
                case 1: // Heart
                    element.moveTo(0, size / 4);
                    element.bezierCurveTo(size / 4, 0, size / 2, size / 4, 0, size);
                    element.bezierCurveTo(-size / 2, size / 4, -size / 4, 0, 0, size / 4);
                    break;
                case 2: // Diamond
                    element.moveTo(0, -size / 2);
                    element.lineTo(size / 2, 0);
                    element.lineTo(0, size / 2);
                    element.lineTo(-size / 2, 0);
                    break;
            }
            
            element.endFill();
            element.x = x;
            element.y = y;
            element.speedX = (Math.random() - 0.5) * 2;
            element.speedY = (Math.random() - 0.5) * 2;
            element.rotationSpeed = (Math.random() - 0.5) * 0.02;
            
            this.creditsContainer.addChild(element);
            this.creditsFloatingElements.push(element);
        }
        
        // Animate floating elements
        this.app.ticker.add(() => {
            if (!this.creditsContainer.visible) return;
            
            this.creditsFloatingElements.forEach(element => {
                element.x += element.speedX;
                element.y += element.speedY;
                element.rotation += element.rotationSpeed;
                
                // Bounce off container edges
                if (element.x < 0 || element.x > 600) element.speedX *= -1;
                if (element.y < 0 || element.y > 700) element.speedY *= -1;
            });
        });
    }
    
    cleanup() {
        console.log('[MENU] Starting cleanup process');
        
        // Stop menu music first
        if (this.audioManager) {
            try {
                console.log('[MENU] Fading out menu music');
                this.audioManager.fadeOutGlobalSound('menu_music', 0.0);
            } catch (error) {
                console.warn('[MENU] Error stopping menu music:', error);
            }
        }
        
        // Stop animations
        if (this.app.ticker) {
            console.log('[MENU] Removing ticker callbacks');
            this.app.ticker.remove(this.updateFloatingItems, this);
            this.app.ticker.remove(this.updateWaterShader, this);
            this.app.ticker.remove(this.updateMarquee, this);
        }
        
        // Hide menu elements instead of destroying them
        console.log('[MENU] Hiding menu elements');
        this.menuContainer.visible = false;
        this.buttonsContainer.style.display = 'none';
        this.buttonsContainer.style.pointerEvents = 'none';
        this.isVisible = false;
        
        // Hide marquee text
        if (this.marqueeText) {
            this.marqueeText.visible = false;
        }
        
        // Hide water background
        if (this.waterBackground) {
            this.waterBackground.visible = false;
        }
        
        // Hide floating items
        if (this.floatingItemsContainer) {
            this.floatingItemsContainer.visible = false;
        }
        
        // Hide credits if visible
        if (this.creditsContainer) {
            this.creditsContainer.visible = false;
        }
        
        console.log('[MENU] Cleanup complete');
    }
    
    destroy() {
        console.log('[MENU] Starting full destroy process');
        // Only destroy everything if we're actually destroying the menu
        if (this.waterBackground) {
            this.waterBackground.destroy();
            this.waterBackground = null;
        }
        
        if (this.floatingItemsContainer) {
            this.floatingItemsContainer.destroy();
            this.floatingItemsContainer = null;
        }
        
        this.floatingItems = [];
        
        if (this.menuContainer) {
            this.menuContainer.destroy();
            this.menuContainer = null;
        }
        
        if (this.creditsContainer) {
            this.creditsContainer.destroy();
            this.creditsContainer = null;
        }
        
        if (this.creditsBackButton && this.creditsBackButton.parentNode) {
            this.creditsBackButton.parentNode.removeChild(this.creditsBackButton);
            this.creditsBackButton = null;
        }
        
        if (this.marqueeText) {
            this.marqueeText.destroy();
            this.marqueeText = null;
        }
        
        if (this.buttonsContainer && this.buttonsContainer.parentNode) {
            this.buttonsContainer.parentNode.removeChild(this.buttonsContainer);
            this.buttonsContainer = null;
        }
        
        if (this.uiManager) {
            this.uiManager.destroy();
            this.uiManager = null;
        }
        
        console.log('[MENU] Full destroy complete');
    }
} 